import React, { useEffect, useState } from 'react';
import { PackageIcon, SearchIcon, PlusIcon, AlertTriangleIcon, BarChart3Icon, EditIcon, TrashIcon } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { StatsCard } from '../components/ui/StatsCard';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { supabaseAdmin } from '../lib/supabase';
import { Product } from '../types';
import { useSortableData } from '../hooks/useSortableData';
import { SortableTableHeader } from '../components/ui/SortableTableHeader';
const PROVEEDORES = ['Laboratorios RD', 'FarmaGlobal', 'MedPharma', 'NutriLife', 'EquiMed'];
// SKU auto-increment prefix
const SKU_PREFIX = 'PRD';
type FormData = {
  nombre: string;
  sku: string;
  stock: string;
  precio: string;
  proveedor: string;
};
const emptyForm: FormData = {
  nombre: '',
  sku: '',
  stock: '',
  precio: '',
  proveedor: PROVEEDORES[0]
};
export function InventarioPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  useEffect(() => {
    fetchProducts();
  }, []);
  const mapProduct = (row: any): Product => ({
    id: row.id,
    nombre: row.nombre,
    sku: row.sku,
    stock: row.stock,
    precio: row.precio,
    proveedor: row.proveedor
  });
  const fetchProducts = async () => {
    setIsLoading(true);
    try {
      // Supabase has a 1000-row default limit — paginate to fetch all products
      const allProducts: Product[] = [];
      const PAGE_SIZE = 1000;
      let from = 0;
      let hasMore = true;
      while (hasMore) {
        const {
          data,
          error
        } = await supabaseAdmin.from('products').select('*').order('nombre').range(from, from + PAGE_SIZE - 1);
        if (error) break;
        if (data && data.length > 0) {
          allProducts.push(...data.map(mapProduct));
          from += PAGE_SIZE;
          hasMore = data.length === PAGE_SIZE;
        } else {
          hasMore = false;
        }
      }
      setProducts(allProducts);
    } finally {
      setIsLoading(false);
    }
  };
  const generateSku = (currentProducts: Product[]) => {
    const existing = currentProducts.map(p => {
      const match = p.sku.match(new RegExp(`^${SKU_PREFIX}-(\\d+)$`, 'i'));
      return match ? parseInt(match[1], 10) : 0;
    }).filter(n => n > 0);
    const nextNum = existing.length > 0 ? Math.max(...existing) + 1 : 1;
    return `${SKU_PREFIX}-${String(nextNum).padStart(3, '0')}`;
  };
  const filteredProducts = products.filter(p => p.nombre.toLowerCase().includes(searchQuery.toLowerCase()) || p.sku.toLowerCase().includes(searchQuery.toLowerCase()) || p.proveedor.toLowerCase().includes(searchQuery.toLowerCase()));
  // Sorting setup
  const {
    items: sortedProducts,
    requestSort,
    sortConfig
  } = useSortableData(filteredProducts, {
    sku: p => p.sku,
    nombre: p => p.nombre,
    proveedor: p => p.proveedor,
    precio: p => p.precio,
    stock: p => p.stock
  });
  const totalProductos = products.length;
  const sinStock = products.filter(p => p.stock === 0).length;
  const valorInventario = products.reduce((sum, p) => sum + p.stock * p.precio, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const totalPages = Math.ceil(sortedProducts.length / ITEMS_PER_PAGE);
  const paginatedProducts = sortedProducts.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const openAddModal = () => {
    setEditingProduct(null);
    setFormData({
      ...emptyForm,
      sku: generateSku(products)
    });
    setIsModalOpen(true);
  };
  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setFormData({
      nombre: product.nombre,
      sku: product.sku,
      stock: String(product.stock),
      precio: String(product.precio),
      proveedor: product.proveedor
    });
    setIsModalOpen(true);
  };
  const handleSave = async () => {
    if (!formData.nombre || !formData.sku || !formData.precio) return;
    setIsSaving(true);
    try {
      const payload = {
        nombre: formData.nombre,
        sku: formData.sku,
        stock: Number(formData.stock) || 0,
        precio: Number(formData.precio),
        proveedor: formData.proveedor
      };
      if (editingProduct) {
        const {
          data
        } = await supabaseAdmin.from('products').update(payload).eq('id', editingProduct.id).select().single();
        if (data) setProducts(prev => prev.map(p => p.id === editingProduct.id ? mapProduct(data) : p));
      } else {
        const {
          data
        } = await supabaseAdmin.from('products').insert(payload).select().single();
        if (data) setProducts(prev => [mapProduct(data), ...prev]);
      }
      setIsModalOpen(false);
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingProduct) return;
    await supabaseAdmin.from('products').delete().eq('id', editingProduct.id);
    setProducts(prev => prev.filter(p => p.id !== editingProduct.id));
    setIsDeleteModalOpen(false);
    setIsModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-1731">
      <PageHeader title="Inventario" subtitle="Control de stock y productos" icon={<PackageIcon className="w-6 h-6" data-id="element-1733" />} data-id="element-1732">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1735" />} onClick={openAddModal} data-id="element-1734">
          Nuevo Producto
        </Button>
      </PageHeader>

      {/* Sin stock alert */}
      {sinStock > 0 && <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl animate-stagger-1" data-id="element-1736">
          <AlertTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0" data-id="element-1737" />
          <p className="text-sm text-amber-800 font-medium" data-id="element-1738">
            {sinStock} producto{sinStock > 1 ? 's' : ''} sin existencia. Revisa
            el inventario.
          </p>
        </div>}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-stagger-1" data-id="element-1739">
        <StatsCard title="Total Productos" value={totalProductos} icon={<PackageIcon className="w-5 h-5" data-id="element-1741" />} variant="primary" data-id="element-1740" />
        <StatsCard title="Sin Existencia" value={sinStock} icon={<AlertTriangleIcon className="w-5 h-5" data-id="element-1743" />} variant="danger" data-id="element-1742" />
        <StatsCard title="Valor Inventario" value={formatCurrency(valorInventario)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-1745" />} variant="success" data-id="element-1744" />
      </div>

      {/* Search */}
      <div className="relative max-w-md animate-stagger-2" data-id="element-1746">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-1747" />
        <input type="text" placeholder="Buscar por código, descripción o proveedor..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4]" data-id="element-1748" />
      </div>

      {/* Table */}
      <Card padding="none" className="animate-stagger-3" data-id="element-1749">
        {filteredProducts.length === 0 ? <div className="p-16 text-center animate-fade-in" data-id="element-1750">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-1751">
              <PackageIcon className="w-8 h-8 text-slate-400" data-id="element-1752" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-1753">
              No hay productos
            </h3>
            <p className="text-slate-500 text-sm" data-id="element-1754">
              No se encontraron productos con los filtros aplicados
            </p>
          </div> : <div className="overflow-x-auto" data-id="element-1755">
            <table className="w-full" data-id="element-1756">
              <thead data-id="element-1757">
                <tr className="bg-slate-50/80 border-b border-slate-100" data-id="element-1758">
                  <SortableTableHeader label="Código" sortKey="sku" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1759" />
                  <SortableTableHeader label="Descripción" sortKey="nombre" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1760" />
                  <SortableTableHeader label="Proveedor" sortKey="proveedor" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1761" />
                  <SortableTableHeader label="Precio" sortKey="precio" align="right" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1762" />
                  <SortableTableHeader label="Existencia" sortKey="stock" align="center" currentSortKey={sortConfig?.key} currentDirection={sortConfig?.direction} onSort={requestSort} data-id="element-1763" />
                  <th className="px-5 py-4 w-20" data-id="element-1764"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-1765">
                {paginatedProducts.map(product => <tr key={product.id} className={`hover:bg-slate-50/50 transition-colors ${product.stock === 0 ? 'bg-rose-50/20' : ''}`} data-id="element-1766">
                    <td className="px-5 py-4 font-mono text-sm font-semibold text-[#2952C4]" data-id="element-1767">
                      {product.sku}
                    </td>
                    <td className="px-5 py-4 font-medium text-slate-900" data-id="element-1768">
                      {product.nombre}
                    </td>
                    <td className="px-5 py-4 text-sm text-slate-600" data-id="element-1769">
                      {product.proveedor}
                    </td>
                    <td className="px-5 py-4 text-right font-medium text-slate-900" data-id="element-1770">
                      {formatCurrency(product.precio)}
                    </td>
                    <td className="px-5 py-4 text-center" data-id="element-1771">
                      <span className={`font-bold text-lg ${product.stock === 0 ? 'text-rose-600' : 'text-slate-700'}`} data-id="element-1772">
                        {product.stock}
                      </span>
                    </td>
                    <td className="px-5 py-4" data-id="element-1773">
                      <div className="flex items-center gap-1" data-id="element-1774">
                        <button onClick={() => openEditModal(product)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" data-id="element-1775">
                          <EditIcon className="w-4 h-4" data-id="element-1776" />
                        </button>
                        <button onClick={() => {
                    setEditingProduct(product);
                    setIsDeleteModalOpen(true);
                  }} className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-1777">
                          <TrashIcon className="w-4 h-4" data-id="element-1778" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </Card>

      <div className="animate-stagger-4" data-id="element-1779">
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredProducts.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-1780" />
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'} size="md" data-id="element-1781">
        <ModalBody data-id="element-1782">
          <div className="space-y-4" data-id="element-1783">
            <div className="grid grid-cols-2 gap-4" data-id="element-1784">
              <Input label="Código (SKU)" value={formData.sku} onChange={e => setFormData({
              ...formData,
              sku: e.target.value
            })} placeholder="PRD-001" data-id="element-1785" />
              <div data-id="element-1786">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-1787">
                  Proveedor
                </label>
                <select value={formData.proveedor} onChange={e => setFormData({
                ...formData,
                proveedor: e.target.value
              })} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1788">
                  {PROVEEDORES.map(p => <option key={p} value={p} data-id="element-1789">
                      {p}
                    </option>)}
                </select>
              </div>
            </div>
            <Input label="Descripción" value={formData.nombre} onChange={e => setFormData({
            ...formData,
            nombre: e.target.value
          })} placeholder="Ej: Amoxicilina 500mg" data-id="element-1790" />
            <div className="grid grid-cols-2 gap-4" data-id="element-1791">
              <Input label="Precio (DOP)" type="number" value={formData.precio} onChange={e => setFormData({
              ...formData,
              precio: e.target.value
            })} placeholder="0" data-id="element-1792" />
              <Input label="Existencia" type="number" value={formData.stock} onChange={e => setFormData({
              ...formData,
              stock: e.target.value
            })} placeholder="0" data-id="element-1793" />
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-1794">
          <Button variant="secondary" onClick={() => setIsModalOpen(false)} data-id="element-1795">
            Cancelar
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.nombre || !formData.sku || !formData.precio} data-id="element-1796">
            {editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Producto" size="sm" data-id="element-1797">
        <ModalBody data-id="element-1798">
          <p className="text-slate-600" data-id="element-1799">
            ¿Eliminar <strong data-id="element-1800">{editingProduct?.nombre}</strong>? Esta acción no
            se puede deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-1801">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-1802">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} data-id="element-1803">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}